// src/pages/ProviderDashboard.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  listenToOpenJobs,
  listenToJobWithPrivate,
  submitBid,
  MIN_BID_GBP,
  listenProviderBids,
  getLeaderboard,
  updateJobStatus,
  listenProviderReviews,
  withdrawBid,
  listenToJobBids,
  listenWeeklyLeague,
  getPreviousWeekLeague,
  getPreviousWeekStart,
  listenYardActivity,
  createNotification,
  getVerifiedCarriers,
  createReferral,
  getMyReferrals,
  expireJobReferrals,
  ensureRivalsAssigned,
  listenActiveChallenge,
  listenChallengeProgress,
  ensureActiveChallengeExists,
} from '../services/firestoreService';
import { jobAreaLabel, licenceForPaperwork } from '../utils/jobPrivacy';
import { SERVICES } from '../data/mockData';
import VerifiedBadge from '../components/VerifiedBadge';
import RegisterCheck from '../components/RegisterCheck';
import LevelBadge from '../components/LevelBadge';
import BadgeShelf from '../components/BadgeShelf';
import AchievementToast from '../components/AchievementToast';
import JobChat from '../components/JobChat';
import '../pages/ProviderDashboard.css';

// Photo strip with lightbox — same component as CustomerDashboard
const PhotoStrip = ({ urls }) => {
  const [lightbox, setLightbox] = React.useState(null);
  if (!urls?.length) return null;
  return (
    <>
      <div className="photo-strip">
        {urls.map((url, i) => (
          <img
            key={i}
            src={url}
            alt={`View ${i + 1} of ${urls.length}`}
            className="photo-strip-thumb"
            onClick={() => setLightbox(url)}
          />
        ))}
      </div>
      {lightbox && (
        <div className="lightbox-overlay" onClick={() => setLightbox(null)}>
          <button className="lightbox-close" onClick={() => setLightbox(null)}>✕</button>
          <img src={lightbox} alt="Full size" className="lightbox-img" onClick={e => e.stopPropagation()} />
        </div>
      )}
    </>
  );
};

const getWeekNumber = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 3 - (d.getDay() + 6) % 7);
  const week1 = new Date(d.getFullYear(), 0, 4);
  return 1 + Math.round(((d.getTime() - week1.getTime()) / 86400000 - 3 + (week1.getDay() + 6) % 7) / 7);
};

const getWeekStartLabel = () => {
  const now = new Date();
  const day = now.getDay();
  const diff = (day === 0 ? -6 : 1) - day;
  const monday = new Date(now);
  monday.setDate(now.getDate() + diff);
  return monday.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
};

const calcCountdown = () => {
  const now = new Date();
  const day = now.getDay();
  const daysUntilMonday = ((8 - day) % 7) || 7;
  const nextMonday = new Date(now);
  nextMonday.setDate(now.getDate() + daysUntilMonday);
  nextMonday.setHours(0, 0, 0, 0);
  const diff = nextMonday - now;
  const days    = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours   = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  return { days, hours, minutes };
};

// Returns the visually-displayed streak — 0 if the carrier has missed a day
const getEffectiveStreak = (currentStreak, lastJobDate) => {
  if (!lastJobDate || !currentStreak) return 0;
  const today = new Date().toISOString().split('T')[0];
  const yd = new Date();
  yd.setDate(yd.getDate() - 1);
  const yesterday = yd.toISOString().split('T')[0];
  return (lastJobDate === today || lastJobDate === yesterday) ? currentStreak : 0;
};

const ProviderDashboard = () => {
  const { user, refreshUser } = useAuth();
  const navigate       = useNavigate();
  const [searchParams] = useSearchParams();

  const [activeTab, setActiveTab]   = useState('jobs');
  const [jobs, setJobs]             = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [myBids, setMyBids]         = useState([]);
  const [reviews, setReviews]       = useState([]);
  const [weeklyLeague, setWeeklyLeague] = useState([]);
  const [prevWinner, setPrevWinner]   = useState(null);
  const [yardBids, setYardBids]       = useState({});
  const [yardActivity, setYardActivity] = useState([]);
  const [countdown, setCountdown]     = useState(calcCountdown());
  const [jobSearch, setJobSearch]       = useState('');
  const [filterService, setFilterService] = useState('All');
  const [filterBudget, setFilterBudget]   = useState('Any');
  const [sortBy, setSortBy]               = useState('newest');
  const [bidModal, setBidModal]       = useState(null);
  const [bidForm, setBidForm]       = useState({ price:'', eta:'', note:'' });
  const [bidError, setBidError]     = useState('');
  const [loading, setLoading]       = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast]           = useState('');
  const [jobStatuses, setJobStatuses]         = useState({});
  const [jobDetails, setJobDetails]           = useState({});
  const [bidsLoaded, setBidsLoaded]           = useState(false);
  const [transitioningJobs, setTransitioningJobs] = useState(new Set());
  const yardUnsubsRef      = useRef({});
  const activeJobUnsubsRef = useRef({});
  const activityUnsubRef   = useRef(null);

  // Highlight state — set when carrier arrives via a referral_received notification
  const [highlightJobId,    setHighlightJobId]    = useState(null);
  const [highlightDismissed, setHighlightDismissed] = useState(false);
  const jobCardRefs = useRef({});

  // Referral state
  const [referralModal,      setReferralModal]      = useState(null);  // job being referred
  const [referralCarriers,   setReferralCarriers]   = useState([]);
  const [referralSearch,     setReferralSearch]     = useState('');
  const [referralSelected,   setReferralSelected]   = useState(null);
  const [referralSubmitting, setReferralSubmitting] = useState(false);
  const [referralStats,      setReferralStats]      = useState({ count: 0, credits: 0 });
  const [myReferredJobs,     setMyReferredJobs]     = useState(new Set());

  // Weekly challenge state
  const [activeChallenge,   setActiveChallenge]   = useState(null);
  const [challengeProgress, setChallengeProgress] = useState(null);

  useEffect(() => { if (!user) navigate('/login'); }, [user, navigate]);
  useEffect(() => { document.title = 'Carrier Dashboard — The WasteGame'; }, []);

  // Read ?tab= and ?highlightJobId= from URL — re-runs when URL params change so
  // notification clicks work even when the carrier is already on the dashboard.
  useEffect(() => {
    const tab   = searchParams.get('tab');
    const hjid  = searchParams.get('highlightJobId');
    if (tab)  setActiveTab(tab);
    if (hjid) { setHighlightJobId(hjid); setHighlightDismissed(false); }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // Countdown timer — updates every minute
  useEffect(() => {
    const interval = setInterval(() => setCountdown(calcCountdown()), 60000);
    return () => clearInterval(interval);
  }, []);

  // Scroll to and highlight a recommended job when highlightJobId is set.
  // If the job no longer exists (taken/cancelled), show a toast and clear the highlight.
  useEffect(() => {
    if (!highlightJobId || highlightDismissed || loading || activeTab !== 'jobs') return;
    if (jobs.length === 0) return; // still loading
    const exists = jobs.some(j => j.id === highlightJobId);
    if (!exists) {
      showToast('The job you were recommended for is no longer available.');
      setHighlightJobId(null);
      return;
    }
    // Small delay so the DOM has painted the cards before we scroll
    const t = setTimeout(() => {
      const el = jobCardRefs.current[highlightJobId];
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 350);
    return () => clearTimeout(t);
  }, [highlightJobId, highlightDismissed, loading, activeTab, jobs]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-assign rivals if any carrier in the current week has none yet.
  // Runs when league data loads and the Yard tab is active.
  const rivalsTriedRef = useRef(false);
  useEffect(() => {
    if (!user || weeklyLeague.length < 2 || rivalsTriedRef.current) return;
    const myEntry = weeklyLeague.find(e => e.providerId === user.uid);
    if (myEntry && !myEntry.rivalId) {
      rivalsTriedRef.current = true;
      // ensureRivalsAssigned writes to weeklyLeague docs; listenWeeklyLeague
      // fires automatically and updates weeklyLeague state — no manual refresh needed.
      ensureRivalsAssigned()
        .catch(err => console.error('[rivals] auto-assign failed:', err));
    }
  }, [weeklyLeague, user?.uid]); // eslint-disable-line react-hooks/exhaustive-deps

  // Weekly challenge — ensure one exists, then listen to it and the carrier's progress
  useEffect(() => {
    if (!user) return;
    ensureActiveChallengeExists().catch(() => {});
    const unsubChallenge = listenActiveChallenge(setActiveChallenge);
    const unsubProgress  = listenChallengeProgress(user.uid, setChallengeProgress);
    return () => { unsubChallenge(); unsubProgress(); };
  }, [user?.uid]); // eslint-disable-line react-hooks/exhaustive-deps

  // Yard activity feed — live across all carriers
  useEffect(() => {
    if (!user) return;
    activityUnsubRef.current = listenYardActivity(setYardActivity);
    return () => { if (activityUnsubRef.current) activityUnsubRef.current(); };
  }, [user?.uid]); // eslint-disable-line react-hooks/exhaustive-deps

  // Real-time open jobs listener — updates jobs state on every Firestore change.
  // Also diffs Yard bid listeners so per-job listeners are added/removed as jobs
  // appear or disappear (accepted, cancelled) without re-subscribing to existing ones.
  useEffect(() => {
    if (!user) return;
    const unsub = listenToOpenJobs((newJobs) => {
      setJobs(newJobs);

      // Diff Yard listeners: remove for gone jobs, add for new jobs
      const newJobIds = new Set(newJobs.map(j => j.id));
      const currentIds = Object.keys(yardUnsubsRef.current);

      // Tear down listeners for jobs no longer open
      currentIds.forEach(id => {
        if (!newJobIds.has(id)) {
          yardUnsubsRef.current[id]();
          delete yardUnsubsRef.current[id];
        }
      });

      // Set up listeners for newly appeared jobs
      newJobs.forEach(job => {
        if (!yardUnsubsRef.current[job.id]) {
          yardUnsubsRef.current[job.id] = listenToJobBids(job.id, (jobBids) => {
            setYardBids(prev => ({ ...prev, [job.id]: jobBids }));
          });
        }
      });
    });
    return () => {
      unsub();
      Object.values(yardUnsubsRef.current).forEach(fn => fn());
      yardUnsubsRef.current = {};
    };
  }, [user?.uid]); // eslint-disable-line react-hooks/exhaustive-deps

  // One-time data load: leaderboard, previous league (for reset notification), referrals.
  // Leaderboard is kept as a one-time fetch — converting to onSnapshot would fire on
  // every user-doc write across all carriers, which is too expensive.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    Promise.all([
      getLeaderboard(),
      getPreviousWeekLeague(),
    ]).then(([lb, prevLeague]) => {
      if (cancelled) return;
      setLeaderboard(lb);
      setPrevWinner(prevLeague[0] || null);
      setLoading(false);

      // Weekly reset notification — fires once per reset per user via localStorage
      const myPrevEntry = prevLeague.find(e => e.providerId === user.uid);
      if (myPrevEntry) {
        const prevWeekStart = getPreviousWeekStart();
        const notifKey = `yard_reset_${prevWeekStart}_${user.uid}`;
        if (!localStorage.getItem(notifKey)) {
          const rank = prevLeague.findIndex(e => e.providerId === user.uid) + 1;
          const msg = rank <= 3
            ? `🏆 You finished #${rank} in The Yard this week! ${myPrevEntry.points} points earned. New week started — can you go one better?`
            : `The Yard weekly league has reset. You finished #${rank} last week. New week, new chance! 💪`;
          createNotification(user.uid, 'yard_weekly', msg, null)
            .then(() => localStorage.setItem(notifKey, '1'));
        }
      }
    });

    // Load referral stats separately (non-blocking)
    getMyReferrals(user.uid).then(refs => {
      if (cancelled) return;
      const completed = refs.filter(r => r.status === 'completed');
      setReferralStats({ count: completed.length, credits: completed.length * 15 });
      setMyReferredJobs(new Set(refs.map(r => r.jobId)));
    }).catch(() => {});

    return () => { cancelled = true; };
  }, [user?.uid]); // eslint-disable-line react-hooks/exhaustive-deps

  // Real-time listener for this carrier's reviews — new reviews appear without refresh
  useEffect(() => {
    if (!user) return;
    const unsub = listenProviderReviews(user.uid, setReviews);
    return unsub;
  }, [user?.uid]); // eslint-disable-line react-hooks/exhaustive-deps

  // Real-time listener for the weekly league — Yard tab points update live
  useEffect(() => {
    if (!user) return;
    const unsub = listenWeeklyLeague(setWeeklyLeague);
    return unsub;
  }, [user?.uid]); // eslint-disable-line react-hooks/exhaustive-deps

  // Real-time listener for provider bids.
  // For each accepted bid, spins up a listener on the public job and its private
  // address so jobDetails and jobStatuses stay live (En Route → Arrived → Completed).
  // Uses the same diff pattern as the Yard listener: only adds/removes listeners
  // for jobs that actually changed, never re-subscribing to existing ones.
  useEffect(() => {
    if (!user) return;
    const unsubBids = listenProviderBids(user.uid, (bids) => {
      setMyBids(bids);
      setBidsLoaded(true);

      const acceptedBids  = bids.filter(b => b.status === 'Accepted' && b.jobId);
      const acceptedJobIds = new Set(acceptedBids.map(b => b.jobId));
      const currentIds    = Object.keys(activeJobUnsubsRef.current);

      // Tear down listeners for jobs no longer in the accepted set
      currentIds.forEach(id => {
        if (!acceptedJobIds.has(id)) {
          activeJobUnsubsRef.current[id]();
          delete activeJobUnsubsRef.current[id];
        }
      });

      // Set up live listeners for newly accepted jobs
      acceptedBids.forEach(bid => {
        if (!activeJobUnsubsRef.current[bid.jobId]) {
          activeJobUnsubsRef.current[bid.jobId] = listenToJobWithPrivate(bid.jobId, (job) => {
            setJobDetails(prev => ({ ...prev, [job.id]: job }));
            setJobStatuses(prev => ({ ...prev, [job.id]: job.status }));
          });
        }
      });
    });
    return () => {
      unsubBids();
      Object.values(activeJobUnsubsRef.current).forEach(fn => fn());
      activeJobUnsubsRef.current = {};
    };
  }, [user?.uid]); // eslint-disable-line react-hooks/exhaustive-deps

  const stats = {
    credits:   user?.credits   || 0,
    rating:    user?.rating    || 0,
    jobs:      user?.jobs      || 0,
    verified:  user?.verified  || false,
    licenceNo: licenceForPaperwork(user?.licenceNo),
    streak:    getEffectiveStreak(user?.currentStreak, user?.lastJobDate),
  };

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  const handleBidSubmit = async () => {
    const isScrapJob = bidModal?.serviceType === 'Scrap Collection';
    if (!bidModal) return;
    if (!isScrapJob && !(Number(bidForm.price) >= MIN_BID_GBP)) {
      setBidError(`Minimum bid is £${MIN_BID_GBP}`);
      return;
    }
    setBidError('');
    if (!stats.verified) {
      showToast('You cannot bid until your waste carrier registration has been checked against the public register and approved — contact info@thewastegame.co.uk');
      return;
    }
    setSubmitting(true);
    try {
      const bidPrice = isScrapJob ? 0 : Number(bidForm.price);
      await submitBid({
        jobId:        bidModal.id,
        providerName: user.name,
        licenceNo:    stats.licenceNo,
        rating:       stats.rating,
        jobs:         stats.jobs,
        credits:      stats.credits,
        phoneNumber:  user.phoneNumber || '',
        profilePhoto: user.profilePhoto || '',
        price:        bidPrice,
        eta:          bidForm.eta,
        note:         bidForm.note,
      }, user.uid);
      // No optimistic myBids update needed — listenProviderBids fires automatically
      // and sets myBids with fresh Firestore data; adding here would duplicate the bid.
      setBidModal(null);
      setBidForm({ price:'', eta:'', note:'' });
      showToast(isScrapJob ? 'Scrap job accepted!' : `Bid of £${bidForm.price} submitted!`);
      refreshUser();
    } catch (err) {
      console.error(err);
      if (err?.message?.startsWith('Minimum bid')) {
        setBidError(err.message);
      } else {
        showToast('Error submitting bid. Please try again.');
      }
    }
    setSubmitting(false);
  };

  const handleWithdrawBid = async (bid) => {
    try {
      await withdrawBid(bid.id);
      // No optimistic state update — listenProviderBids fires automatically after
      // the Firestore write and replaces myBids with fresh data (including the
      // now-Withdrawn bid). Manually filtering here would be overwritten by the
      // listener anyway, producing a race condition.
      showToast('Bid withdrawn successfully.');
    } catch (err) {
      console.error(err);
      showToast('Error withdrawing bid.');
    }
  };

  // Only count bids that are actively in play — Withdrawn bids must not block re-bidding.
  const alreadyBid      = (jobId) => myBids.some(b => b.jobId === jobId && b.status !== 'Withdrawn');
  const alreadyReferred = (jobId) => myReferredJobs.has(jobId);
  const recycleColor = r => r === 'High' ? 'badge-teal' : r === 'Medium' ? 'badge-amber' : 'badge-muted';

  const handleOpenReferralModal = async (job) => {
    setReferralModal(job);
    setReferralSearch('');
    setReferralSelected(null);
    if (referralCarriers.length === 0) {
      try {
        const carriers = await getVerifiedCarriers(user.uid);
        setReferralCarriers(carriers);
      } catch (err) {
        console.error('[referral] getVerifiedCarriers failed:', err);
      }
    }
  };

  const handleSubmitReferral = async () => {
    if (!referralSelected || !referralModal) return;
    setReferralSubmitting(true);
    try {
      await createReferral({
        jobId:        referralModal.id,
        referrerId:   user.uid,
        referredId:   referralSelected.id,
        referrerName: user.name,
        referredName: referralSelected.name,
        jobTitle:     referralModal.serviceType,
        pickup:       jobAreaLabel(referralModal) || 'your area',
      });
      setMyReferredJobs(prev => new Set([...prev, referralModal.id]));
      setReferralModal(null);
      showToast(`Recommendation sent to ${referralSelected.name}!`);
    } catch (err) {
      console.error('[referral] submit failed:', err.code, err.message, err);
      showToast('Failed to send recommendation. Please try again.');
    }
    setReferralSubmitting(false);
  };

  const JOB_STAGES = ['Accepted', 'En Route', 'Arrived', 'Completed'];

  const handleJobProgress = async (bid, newStatus) => {
    // Guard: prevent double-click while Firestore write is in flight
    if (transitioningJobs.has(bid.jobId)) return;
    setTransitioningJobs(prev => new Set([...prev, bid.jobId]));
    try {
      await updateJobStatus(bid.jobId, newStatus);
      if (newStatus === 'Completed') {
        const serviceType = jobDetails[bid.jobId]?.serviceType || bid.serviceType || '';
        const svc = SERVICES.find(s => s.name === serviceType);
        const credits = svc?.credits || 10;
        // Eco Credits, jobs count and challenge progress are awarded once per job by
        // the onJobUpdated Cloud Function; the browser can no longer write them.
        // Expire any pending referrals for this job that weren't already completed
        expireJobReferrals(bid.jobId).catch(() => {});
        showToast(`Job complete! +${credits} Eco Credits earned.`);
        // Give the Cloud Function a moment before re-reading the profile.
        setTimeout(() => { refreshUser(); }, 3000);
      } else {
        showToast(`Status updated: ${newStatus}`);
      }
      setJobStatuses(prev => ({ ...prev, [bid.jobId]: newStatus }));
    } catch (err) {
      console.error(err);
      showToast('Error updating job status.');
    }
    setTransitioningJobs(prev => { const n = new Set(prev); n.delete(bid.jobId); return n; });
  };

  const parseBudget = (b) => {
    const n = parseFloat(String(b || '').replace(/[^0-9.]/g, ''));
    return isNaN(n) ? null : n;
  };

  const filteredJobs = jobs
    .filter(job => job.status !== 'Cancelled')
    .filter(job => {
      if (jobSearch) {
        const q = jobSearch.toLowerCase();
        const desc = (job.description || '').toLowerCase();
        const loc  = (jobAreaLabel(job) || '').toLowerCase();
        if (!desc.includes(q) && !loc.includes(q)) return false;
      }
      if (filterService !== 'All' && job.serviceType !== filterService) return false;
      if (filterBudget !== 'Any') {
        const b = parseBudget(job.budget);
        if (b === null) return false;
        if (filterBudget === 'Under £50'   && b >= 50)               return false;
        if (filterBudget === '£50–£100'    && (b < 50  || b > 100))  return false;
        if (filterBudget === '£100–£200'   && (b < 100 || b > 200))  return false;
        if (filterBudget === '£200+'       && b < 200)               return false;
      }
      return true;
    })
    .sort((a, b) => {
      if (sortBy === 'budget-high') return (parseBudget(b.budget) || 0) - (parseBudget(a.budget) || 0);
      if (sortBy === 'budget-low')  return (parseBudget(a.budget) || 0) - (parseBudget(b.budget) || 0);
      return (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0);
    });

  const filtersActive = jobSearch || filterService !== 'All' || filterBudget !== 'Any' || sortBy !== 'newest';

  // Pre-compute split lists used by multiple tabs / tab badge counts.
  // IMPORTANT: require js to be a known in-progress value — never treat undefined
  // (job status not yet loaded) as in-progress. The previous `!js || IN_PROGRESS.includes(js)`
  // caused a flash-of-1 badge on page load: listenProviderBids fires before the job listener,
  // so for the brief window between the two snapshots, js is undefined and old accepted bids
  // for completed jobs incorrectly passed the filter.
  const IN_PROGRESS   = ['Accepted', 'En Route', 'Arrived'];
  const activeJobBids = myBids.filter(b => {
    if (b.status !== 'Accepted') return false;
    const js = jobStatuses[b.jobId];
    return js && IN_PROGRESS.includes(js);
  });
  const offeredBids = myBids.filter(b => !b.status || b.status === 'Offered');
  const leaderboardUnranked = leaderboard.length > 0
    && leaderboard.every(c => !(Number(c.credits) > 0));

  if (!user) return null;

  return (
    <div className="dashboard-page">
      {toast && <div className="toast">{toast}</div>}
      <AchievementToast provider={user} />
      <div className="page-wrapper">

        {/* STRIPE SETUP BANNER — shown until payouts are connected */}
        {(!user.stripeAccountId || user.stripeOnboardingComplete !== true) && (
          <div className="unverified-banner" style={{display:'flex', alignItems:'center', justifyContent:'space-between', gap:'12px', flexWrap:'wrap', margin:'0 0 16px', padding:'12px 16px', fontSize:'0.9rem'}}>
            <span>⚠️ <strong>Set up Stripe to get paid</strong> — customers can't pay you until your payout account is connected.</span>
            <Link to="/provider-profile/edit#payment-setup" className="btn btn-primary btn-sm">💳 Set up payments</Link>
          </div>
        )}

        {/* HEADER */}
        <div className="pd-hero">
          <div className="pd-hero-info">
            <h1 className="section-title">CARRIER <span>DASHBOARD</span></h1>
            <p className="text-muted" style={{marginBottom:'8px'}}>Welcome back, {user.name}</p>
            <div style={{display:'flex', alignItems:'center', gap:'10px', flexWrap:'wrap', marginBottom:'8px'}}>
              {stats.verified
                ? <VerifiedBadge licenceNo={stats.licenceNo} size="md" />
                : <div className="unverified-banner">⛔ Your account cannot bid until your waste carrier registration has been checked against the public register and approved — <a href="mailto:info@thewastegame.co.uk" className="unverified-link">contact info@thewastegame.co.uk</a>. <RegisterCheck /></div>
              }
              {stats.verified && !stats.licenceNo && (
                <span className="text-muted" style={{fontSize:'0.8rem'}}>
                  No waste carrier licence number on your profile — email info@thewastegame.co.uk to add it. It is required on waste transfer notes. <RegisterCheck />
                </span>
              )}
              <Link to="/provider-profile/edit" className="btn btn-outline btn-sm">✏️ Edit Profile</Link>
              {user.stripeAccountId && user.stripeOnboardingComplete === true && (
                <Link to="/provider-profile/edit#payment-setup" className="btn btn-outline btn-sm">💳 Payments</Link>
              )}
              <Link to={`/carrier/${user.uid}`} className="btn btn-outline btn-sm">👤 View Public Profile</Link>
            </div>
            <div style={{marginTop:'4px'}}>
              <LevelBadge jobs={stats.jobs} size="md" showProgress />
            </div>
          </div>
          <div className="provider-stats">
            <div className="pstat"><div className="pstat-val">{stats.credits}</div><div className="pstat-lbl">Eco Credits</div></div>
            <div className="pstat"><div className="pstat-val amber">{stats.rating || '—'}</div><div className="pstat-lbl">Rating</div></div>
            <div className="pstat"><div className="pstat-val">{stats.jobs}</div><div className="pstat-lbl">Jobs Done</div></div>
            {stats.streak > 0 && (
              <div className="pstat">
                <div className="pstat-val" style={{color:'var(--amber)'}}>🔥 {stats.streak}</div>
                <div className="pstat-lbl">Day Streak</div>
              </div>
            )}
            {referralStats.count > 0 && (
              <div className="pstat">
                <div className="pstat-val" style={{color:'var(--teal)'}}>🤝 {referralStats.count}</div>
                <div className="pstat-lbl">Referrals</div>
              </div>
            )}
          </div>
        </div>

        {/* BADGES + PLEDGE — compact card */}
        <div className="card pd-eco-card">
          <BadgeShelf provider={user} compact />
          <div className="pd-pledge-strip">
            🚫 <strong>Zero fly-tipping pledge</strong> — waste carrier licence visible on every bid · Only use licensed disposal facilities
          </div>
        </div>

        {/* TABS */}
        <div className="provider-tabs">
          <button className={`ptab ${activeTab === 'jobs' ? 'active' : ''}`} onClick={() => setActiveTab('jobs')}>
            Open Jobs{!loading && ` (${filtersActive ? `${filteredJobs.length}/` : ''}${jobs.length})`}
          </button>
          <button className={`ptab ${activeTab === 'active' ? 'active' : ''}`} onClick={() => setActiveTab('active')}>
            Active Jobs{bidsLoaded && activeJobBids.length > 0 && <span className="ptab-badge">{activeJobBids.length}</span>}
          </button>
          <button className={`ptab ${activeTab === 'mybids' ? 'active' : ''}`} onClick={() => setActiveTab('mybids')}>
            My Bids{offeredBids.length > 0 && ` (${offeredBids.length})`}
          </button>
          <button className={`ptab ${activeTab === 'yard' ? 'active' : ''}`} onClick={() => setActiveTab('yard')}>⚔️ The Yard</button>
          <button className={`ptab ${activeTab === 'leaderboard' ? 'active' : ''}`} onClick={() => setActiveTab('leaderboard')}>🏆 Leaderboard</button>
          <button className={`ptab ${activeTab === 'reviews' ? 'active' : ''}`} onClick={() => setActiveTab('reviews')}>⭐ Reviews{reviews.length > 0 && ` (${reviews.length})`}</button>
        </div>

        {/* JOBS TAB */}
        {activeTab === 'jobs' && (
          <div className="jobs-list">
            {/* SEARCH + FILTER BAR */}
            <div className="jobs-filter-bar">
              <input
                className="jfb-search"
                type="text"
                placeholder="🔍 Search jobs by description or location..."
                value={jobSearch}
                onChange={e => setJobSearch(e.target.value)}
              />
              <div className="jfb-dropdowns">
                <select className="jfb-select" value={filterService} onChange={e => setFilterService(e.target.value)}>
                  <option value="All">All services</option>
                  <option>Waste Collection</option>
                  <option>Man &amp; Van</option>
                  <option>House Clearance</option>
                  <option>Garden Clearance</option>
                  <option>Scrap Collection</option>
                  <option>Packing</option>
                </select>
                <select className="jfb-select" value={filterBudget} onChange={e => setFilterBudget(e.target.value)}>
                  <option value="Any">Any budget</option>
                  <option value="Under £50">Under £50</option>
                  <option value="£50–£100">£50–£100</option>
                  <option value="£100–£200">£100–£200</option>
                  <option value="£200+">£200+</option>
                </select>
                <select className="jfb-select" value={sortBy} onChange={e => setSortBy(e.target.value)}>
                  <option value="newest">Newest first</option>
                  <option value="budget-high">Budget: high → low</option>
                  <option value="budget-low">Budget: low → high</option>
                </select>
              </div>
              {filtersActive && (
                <button className="jfb-clear" onClick={() => { setJobSearch(''); setFilterService('All'); setFilterBudget('Any'); setSortBy('newest'); }}>
                  ✕ Clear filters
                </button>
              )}
            </div>

            {loading && <p className="text-muted">Loading jobs...</p>}
            {!loading && jobs.length === 0 && (
              <div className="card" style={{textAlign:'center',padding:'40px'}}>
                <p style={{fontSize:'1.5rem',marginBottom:'10px'}}>🎉</p>
                <p className="text-muted">No open jobs right now — check back soon!</p>
              </div>
            )}
            {!loading && jobs.length > 0 && filteredJobs.length === 0 && (
              <div className="card" style={{textAlign:'center',padding:'30px'}}>
                <p className="text-muted">No jobs match your filters.</p>
              </div>
            )}
            {filteredJobs.map(job => {
              const isHighlighted = !highlightDismissed && job.id === highlightJobId;
              return (
              <div
                key={job.id}
                ref={el => { if (el) jobCardRefs.current[job.id] = el; else delete jobCardRefs.current[job.id]; }}
                className={`job-card card${isHighlighted ? ' job-card-recommended' : ''}`}
              >
                {isHighlighted && (
                  <div className="jc-recommend-pill">
                    ✨ You were recommended for this job
                    <button
                      className="jc-recommend-dismiss"
                      onClick={() => setHighlightDismissed(true)}
                      aria-label="Dismiss"
                    >✕</button>
                  </div>
                )}
                <div className="jc-header">
                  <div className="jc-title-row">
                    <span className="jc-icon">{job.icon || '🗑️'}</span>
                    <div>
                      <div className="jc-title">{job.serviceType}</div>
                      <div className="jc-loc">📍 {jobAreaLabel(job) || 'Area unavailable'}</div>
                    </div>
                  </div>

                  <div className="jc-badges">
                    {job.recycleRating && <span className={`badge ${recycleColor(job.recycleRating)}`}>♻️ {job.recycleRating} potential</span>}
                    {job.credits && <span className="badge badge-teal">+{job.credits} credits</span>}
                    <span className="badge badge-muted">💬 {job.bidCount || 0} bids</span>
                  </div>
                </div>
                <div className="jc-desc">{job.description}</div>
                {job.imageUrls?.length > 0 && <PhotoStrip urls={job.imageUrls} />}
                <div className="jc-meta">
                  <span>📅 {job.date || 'Flexible'}</span>
                  <span>💰 {job.serviceType === 'Scrap Collection' ? 'FREE — Scrap Collection' : (job.budget || 'Open to offers')}</span>
                </div>
                {/* THE YARD — live mini bid leaderboard */}
                <div className="yard-section">
                  <div className="yard-header">
                    <span className="yard-title">⚔️ THE YARD</span>
                    <span className="yard-count">
                      {(yardBids[job.id] || []).length} carrier{(yardBids[job.id] || []).length !== 1 ? 's' : ''} bidding
                    </span>
                  </div>
                  {(yardBids[job.id] || []).length === 0 ? (
                    <div className="yard-empty">No bids yet — be the first in The Yard!</div>
                  ) : (
                    <div className="yard-rows">
                      {(yardBids[job.id] || []).map((bid, idx) => (
                        <div key={bid.id} className={`yard-row ${bid.providerId === user.uid ? 'yard-row-me' : ''}`}>
                          <span className="yard-position">
                            {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : `#${idx + 1}`}
                          </span>
                          <span className="yard-carrier">{bid.providerName}</span>
                          <span className="yard-eco">★ {bid.rating || 0}</span>
                          {/* Only show price for own bid — rival prices are hidden to preserve competitive integrity */}
                          <span className="yard-price">
                            {bid.providerId === user.uid ? `£${bid.price}` : '✓'}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="jc-footer">
                  {alreadyBid(job.id)
                    ? <span className="badge badge-teal" style={{padding:'8px 16px'}}>✓ Bid Submitted</span>
                    : <button className="btn btn-primary btn-sm" onClick={() => { setBidError(''); setBidModal(job); }}>BID NOW</button>
                  }
                  {alreadyReferred(job.id)
                    ? <span className="badge badge-muted jc-refer-badge">✓ Recommended</span>
                    : <button className="btn btn-outline btn-sm jc-refer-btn" onClick={() => handleOpenReferralModal(job)}>👥 Recommend</button>
                  }
                </div>
              </div>
              );
            })}
          </div>
        )}

        {/* THE YARD TAB — weekly league */}
        {activeTab === 'yard' && (() => {
          const myFormGuide = myBids
            .slice()
            .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0))
            .slice(0, 5)
            .map(b => b.status === 'Accepted' ? 'win' : b.status === 'Offered' ? 'pending' : 'loss');

          const myLeagueEntry = weeklyLeague.find(e => e.providerId === user.uid);
          const rivalEntry    = myLeagueEntry?.rivalId
            ? weeklyLeague.find(e => e.providerId === myLeagueEntry.rivalId)
            : null;
          const myPoints      = myLeagueEntry?.points || 0;
          const rivalPoints   = rivalEntry?.points ?? myLeagueEntry?.rivalPoints ?? null;
          const gap           = rivalPoints !== null ? myPoints - rivalPoints : null;
          const myJobsWon     = myLeagueEntry?.jobsWon || 0;
          const rivalJobsWon  = rivalEntry?.jobsWon || 0;

          return (
          <div className="yard-tab">
            <div className="yard-tab-header">
              <div className="yard-tab-title">THE YARD — WEEK {getWeekNumber()}</div>
              <div className="yard-tab-sub">Week starting {getWeekStartLabel()}</div>
            </div>

            {/* Previous week champion */}
            {prevWinner && (
              <div className="yard-champion-banner">
                <div className="ycb-trophy">🏆</div>
                <div className="ycb-content">
                  <div className="ycb-label">Last Week's Champion</div>
                  <div className="ycb-name">{prevWinner.providerName}</div>
                  <div className="ycb-stats">
                    {prevWinner.points} pts · {prevWinner.jobsWon || 0} job{prevWinner.jobsWon !== 1 ? 's' : ''} won · Week of {prevWinner.weekStart}
                  </div>
                </div>
              </div>
            )}

            {/* Countdown to reset */}
            <div className="yard-countdown">
              ⏱ League resets in{' '}
              <strong>
                {countdown.days > 0 ? `${countdown.days}d ` : ''}{countdown.hours}h {countdown.minutes}m
              </strong>
            </div>

            {/* 🎯 WEEKLY CHALLENGE CARD */}
            {activeChallenge && (() => {
              const progressId  = challengeProgress?.challengeId === activeChallenge.templateId ? challengeProgress : null;
              const current     = progressId?.current || 0;
              const target      = activeChallenge.target || 1;
              const completed   = progressId?.completed || false;
              const pct         = Math.min(100, Math.round((current / target) * 100));
              const endsAt      = activeChallenge.endsAt?.toDate ? activeChallenge.endsAt.toDate() : null;
              const msLeft      = endsAt ? endsAt - new Date() : null;
              const daysLeft    = msLeft ? Math.floor(msLeft / 86400000) : null;
              const hoursLeft   = msLeft ? Math.floor((msLeft % 86400000) / 3600000) : null;
              const timeLabel   = daysLeft > 0 ? `${daysLeft}d ${hoursLeft}h left` : hoursLeft > 0 ? `${hoursLeft}h left` : msLeft > 0 ? 'Less than 1h left' : 'Ended';
              return (
                <div className={`challenge-card ${completed ? 'challenge-card-done' : ''}`}>
                  <div className="cc-header">
                    <span className="cc-icon">🎯</span>
                    <div className="cc-title-block">
                      <div className="cc-label">THIS WEEK'S CHALLENGE</div>
                      <div className="cc-title">{activeChallenge.title}</div>
                    </div>
                    {completed && <span className="cc-done-badge">✅ DONE</span>}
                  </div>
                  <p className="cc-desc">{activeChallenge.description}</p>
                  <div className="cc-progress-row">
                    <span className="cc-progress-text">{current} / {target}</span>
                    <span className="cc-time">{timeLabel}</span>
                  </div>
                  <div className="cc-bar-bg">
                    <div className="cc-bar-fill" style={{ width: `${pct}%` }} />
                  </div>
                  <div className="cc-reward">
                    {completed
                      ? `🏆 Bonus claimed — +${activeChallenge.rewardEcoCredits} Eco Credits!`
                      : `🌿 Complete for +${activeChallenge.rewardEcoCredits} Eco Credits`}
                  </div>
                </div>
              );
            })()}

            {/* ⚔️ RIVAL CARD */}
            {myLeagueEntry && myLeagueEntry.rivalId && (rivalEntry || myLeagueEntry.rivalName) ? (
              <div className={`rival-card ${gap !== null && gap < 0 ? 'rival-card-danger' : 'rival-card-ahead'}`}>
                <div className="rc-sword">⚔️</div>
                <div className="rc-body">
                  <div className="rc-label">YOUR RIVAL THIS WEEK</div>
                  <div className="rc-rival-header">
                    <div className="rc-avatar">
                      {(myLeagueEntry.rivalPhoto || rivalEntry?.profilePhoto)
                        ? <img src={myLeagueEntry.rivalPhoto || rivalEntry.profilePhoto} alt={myLeagueEntry.rivalName} className="rc-rival-photo" />
                        : <span>{myLeagueEntry.rivalName?.charAt(0) || '?'}</span>
                      }
                    </div>
                    <div className="rc-rival-identity">
                      <div className="rc-rival-name">
                        {myLeagueEntry.rivalName}
                        <LevelBadge jobs={rivalEntry?.jobsWon || 0} size="sm" />
                      </div>
                      <Link to={`/carrier/${myLeagueEntry.rivalId}`} className="rc-view-profile">
                        View profile →
                      </Link>
                    </div>
                  </div>
                  <div className="rc-stats">
                    <div className="rc-stat">
                      <div className="rc-stat-val rc-mine">{myPoints}</div>
                      <div className="rc-stat-lbl">Your pts</div>
                    </div>
                    <div className={`rc-gap ${gap !== null && gap < 0 ? 'rc-gap-behind' : 'rc-gap-ahead'}`}>
                      {gap === null ? '—'
                        : gap === 0 ? 'LEVEL'
                        : gap > 0   ? `+${gap} ahead`
                        : `${Math.abs(gap)} behind`}
                    </div>
                    <div className="rc-stat">
                      <div className="rc-stat-val rc-rival">{rivalPoints ?? '—'}</div>
                      <div className="rc-stat-lbl">Their pts</div>
                    </div>
                  </div>
                  <div className="rc-h2h">
                    <span className="rc-h2h-label">HEAD TO HEAD — JOBS WON THIS WEEK</span>
                    <div className="rc-h2h-scores">
                      <span className={`rc-h2h-mine ${myJobsWon > rivalJobsWon ? 'rc-h2h-winning' : ''}`}>
                        {myJobsWon} YOU
                      </span>
                      <span className="rc-h2h-sep">vs</span>
                      <span className={`rc-h2h-rival ${rivalJobsWon > myJobsWon ? 'rc-h2h-rival-winning' : ''}`}>
                        THEM {rivalJobsWon}
                      </span>
                    </div>
                  </div>
                  {user?.weeklyRivalWins > 0 && (
                    <div className="rc-win-flag">
                      ⚔️ {user.weeklyRivalWins} rival {user.weeklyRivalWins === 1 ? 'win' : 'wins'} this season
                    </div>
                  )}
                  <div className="rc-taunt">
                    {gap === null || gap === 0
                      ? '🔥 You\'re neck and neck — every job counts!'
                      : gap > 0
                      ? `💪 You're ahead — keep the pressure on and don't let them close the gap!`
                      : `😤 They're beating you right now. Win the next job and take it back!`}
                  </div>
                </div>
              </div>
            ) : myLeagueEntry && !myLeagueEntry.rivalId ? (
              <div className="rival-card rival-card-none">
                <div className="rc-sword">⚔️</div>
                <div className="rc-body">
                  <div className="rc-label">YOUR RIVAL</div>
                  <div className="rc-rival-name" style={{color:'var(--muted)'}}>No rival yet</div>
                  <div className="rc-taunt">Win a job to get on the board and earn a rival!</div>
                </div>
              </div>
            ) : null}

            <div className="league-table">
              {weeklyLeague.length === 0 ? (
                <div className="league-empty">No points earned this week yet — win a job to get on the board!</div>
              ) : weeklyLeague.map((entry, idx) => {
                const maxPoints = weeklyLeague[0]?.points || 1;
                const isMe = entry.providerId === user.uid;
                return (
                  <div key={entry.id} className={`league-row ${isMe ? 'league-row-me' : ''}`}>
                    <div className={`league-pos lp-${idx < 3 ? idx + 1 : ''}`}>
                      {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : idx + 1}
                    </div>
                    <div className="league-avatar">
                      {entry.profilePhoto
                        ? <img src={entry.profilePhoto} alt={entry.providerName} className="league-avatar-img" />
                        : <div className="league-avatar-initials">{entry.providerName?.charAt(0) || '?'}</div>
                      }
                    </div>
                    <div className="league-info">
                      <div className="league-name">
                        {entry.providerName}
                        {isMe && <span className="badge badge-teal">YOU</span>}
                      </div>
                      <div className="league-bar-bg">
                        <div className="league-bar" style={{ width: `${(entry.points / maxPoints) * 100}%` }} />
                      </div>
                      <div className="league-meta">
                        {entry.jobsWon || 0} job{entry.jobsWon !== 1 ? 's' : ''} won this week
                      </div>
                      {isMe && myFormGuide.length > 0 && (
                        <div className="form-guide">
                          {myFormGuide.map((result, i) => (
                            <span key={i} className={`form-dot fd-${result}`} title={result} />
                          ))}
                          <span className="form-guide-label">Last {myFormGuide.length} bids</span>
                        </div>
                      )}
                    </div>
                    <div className="league-points">
                      <div className="league-pts-val">{entry.points}</div>
                      <div className="league-pts-lbl">pts</div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="points-guide">
              <div className="pg-title">HOW TO EARN POINTS</div>
              <div className="pg-item"><span className="pg-pts">+10</span><span>Win a job (bid accepted)</span></div>
              <div className="pg-item"><span className="pg-pts">+5</span><span>Receive a 5-star review</span></div>
              <div className="pg-item"><span className="pg-pts">+3</span><span>Complete a job with 100% eco rate</span></div>
              <div className="pg-item"><span className="pg-pts">+2</span><span>Be the first carrier into The Yard on a job</span></div>
              <div className="pg-item"><span className="pg-pts">+1</span><span>🔥 Streak bonus — win a job on 3+ consecutive days</span></div>
            </div>

            {/* Live yard activity feed */}
            <div className="yard-activity">
              <div className="yard-activity-title">⚡ YARD ACTIVITY</div>
              {yardActivity.length === 0 ? (
                <div className="yard-activity-empty">No activity yet — be the first carrier in The Yard!</div>
              ) : yardActivity.map(n => (
                <div key={n.id} className="yard-activity-item">
                  <span className="yai-time">
                    {n.createdAt?.toDate
                      ? n.createdAt.toDate().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
                      : '—'}
                  </span>
                  <span className="yai-msg">{n.message?.replace(' Check your position!', '')}</span>
                </div>
              ))}
            </div>
          </div>
          );
        })()}

        {/* LEADERBOARD TAB */}
        {activeTab === 'leaderboard' && (
          <div className="card">
            <h2 className="card-title">ECO <span>LEADERBOARD</span></h2>
            <p className="text-muted" style={{marginBottom:'20px',fontSize:'0.85rem'}}>
              Ranked by Eco Credits. Waste carrier licence numbers are public on every bid.
            </p>
            {loading && <p className="text-muted">Loading...</p>}
            {leaderboard.length === 0 && !loading && <p className="text-muted">No carriers yet — be the first!</p>}
            {leaderboard.map((c, i) => (
              <Link key={c.id} to={`/carrier/${c.id}`} style={{textDecoration:'none',display:'block'}}>
              <div className={`lb-item ${c.id === user?.uid ? 'lb-me' : ''}`}>
                <div className={leaderboardUnranked ? 'lb-rank' : `lb-rank r${i < 3 ? i+1 : ''}`}>{leaderboardUnranked ? '-' : i + 1}</div>
                <div className={`lb-avatar ${c.id === user?.uid ? 'lb-av-me' : ''}`}>
                  {c.name?.charAt(0) || '?'}
                </div>
                <div className="lb-info">
                  <div className="lb-name">
                    {c.name}
                    {c.id === user?.uid && <span className="badge badge-teal">YOU</span>}
                    {c.verified && <span style={{fontSize:'0.8rem'}}>✅</span>}
                    <LevelBadge jobs={c.jobs || 0} size="sm" />
                  </div>
                  <div className="lb-meta">{c.jobs || 0} jobs · ★ {c.rating || 0}</div>
                  <div className="lb-bar-bg">
                    <div className="lb-bar-fill" style={{width:`${leaderboard[0]?.credits ? (c.credits/leaderboard[0].credits)*100 : 0}%`}} />
                  </div>
                </div>
                <div className="lb-credits">
                  <div className="lb-cred-val">{c.credits || 0}</div>
                  <div className="lb-cred-lbl">credits</div>
                </div>
              </div>
              </Link>
            ))}
          </div>
        )}

        {/* ACTIVE JOBS TAB */}
        {activeTab === 'active' && (
          <div>
            {activeJobBids.length === 0 ? (
              <div className="card pd-empty-state">
                <p style={{fontSize:'2rem',marginBottom:'10px'}}>🚐</p>
                <p className="text-muted">No active jobs right now.</p>
                <p className="text-muted" style={{fontSize:'0.8rem',marginTop:'4px'}}>Win a bid and it'll appear here.</p>
              </div>
            ) : activeJobBids.map((bid, i) => {
              const currentStatus = jobStatuses[bid.jobId] || 'Accepted';
              const stageIndex    = JOB_STAGES.indexOf(currentStatus);
              const job           = jobDetails[bid.jobId];
              return (
                <div key={i} className="active-job-panel">
                  <div className="ajp-header">
                    <div>
                      <div className="ajp-type">{job?.serviceType || bid.serviceType || 'Job'}</div>
                      <div className="ajp-price">£{bid.price}{bid.eta ? ` · ETA ${bid.eta}` : ''}</div>
                    </div>
                    <span className="badge badge-amber">{currentStatus}</span>
                  </div>
                  {job && (
                    <div className="ajp-job-details">
                      <span>📍 {job.pickupAddress || job.pickup || job.pickupArea || 'Address unavailable'}</span>
                      {(job.dropoffAddress || job.dropoff || job.dropoffArea) && (
                        <span>🏁 {job.dropoffAddress || job.dropoff || job.dropoffArea}</span>
                      )}
                      <span>📋 {job.description}</span>
                      {job.date && <span>📅 {job.date}</span>}
                    </div>
                  )}
                  <div className="ajp-stages">
                    {JOB_STAGES.map((stage, si) => (
                      <div key={stage} className={`ajp-stage ${si < stageIndex ? 'done' : si === stageIndex ? 'active' : 'pending'}`}>
                        <div className="ajp-stage-dot" />
                        <div className="ajp-stage-label">{stage}</div>
                      </div>
                    ))}
                  </div>
                  <div className="ajp-actions">
                    {currentStatus === 'Accepted' && (
                      <button className="btn btn-primary btn-full"
                        onClick={() => handleJobProgress(bid, 'En Route')}
                        disabled={transitioningJobs.has(bid.jobId)}>
                        {transitioningJobs.has(bid.jobId) ? 'UPDATING...' : '🚐 ON MY WAY'}
                      </button>
                    )}
                    {currentStatus === 'En Route' && (
                      <button className="btn btn-primary btn-full"
                        onClick={() => handleJobProgress(bid, 'Arrived')}
                        disabled={transitioningJobs.has(bid.jobId)}>
                        {transitioningJobs.has(bid.jobId) ? 'UPDATING...' : "📍 I'VE ARRIVED"}
                      </button>
                    )}
                    {currentStatus === 'Arrived' && (
                      <button className="btn btn-primary btn-full"
                        onClick={() => handleJobProgress(bid, 'Completed')}
                        disabled={transitioningJobs.has(bid.jobId)}>
                        {transitioningJobs.has(bid.jobId) ? 'UPDATING...' : '✅ COLLECTION COMPLETE'}
                      </button>
                    )}
                  </div>
                  {job?.customerId && (
                    <div style={{marginTop:'16px'}}>
                      <div className="ajp-chat-title">MESSAGE YOUR <span style={{color:'var(--teal)'}}>CUSTOMER</span></div>
                      <JobChat jobId={bid.jobId} currentUser={user} otherUserId={job.customerId} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* MY BIDS TAB — offered/pending bids only */}
        {activeTab === 'mybids' && (
          <div className="card">
            <h2 className="card-title">MY <span>BIDS</span></h2>
            <p className="text-muted" style={{fontSize:'0.82rem',marginTop:'-12px',marginBottom:'16px'}}>
              Bids you've placed that are still waiting for a decision
            </p>
            {offeredBids.length === 0 ? (
              <div className="pd-empty-state" style={{padding:'20px 0'}}>
                <p className="text-muted">No pending bids — browse Open Jobs to bid on a job.</p>
              </div>
            ) : offeredBids.map((bid, i) => {
              const job = jobDetails[bid.jobId] || jobs.find(j => j.id === bid.jobId);
              const area = jobAreaLabel(job);
              const createdDate = bid.createdAt?.toDate
                ? bid.createdAt.toDate().toLocaleDateString('en-GB', {day:'numeric',month:'short'})
                : '';
              return (
                <div key={i} className="bid-pending-card">
                  <div className="bpc-left">
                    <div className="bpc-service">{job?.serviceType || bid.serviceType || 'Job'}</div>
                    <div className="bpc-loc">{area ? `📍 ${area}` : ''}</div>
                    {job?.description && <div className="bpc-desc">{job.description}</div>}
                  </div>
                  <div className="bpc-right">
                    <div className="bpc-price">£{bid.price}</div>
                    {bid.eta && <div className="bpc-eta">ETA {bid.eta}</div>}
                    {createdDate && <div className="bpc-date">{createdDate}</div>}
                    <span className="badge badge-amber" style={{marginTop:'6px'}}>Pending</span>
                    <button className="btn-withdraw" onClick={() => handleWithdrawBid(bid)}>
                      Withdraw
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* REVIEWS TAB */}
        {activeTab === 'reviews' && (
          <div className="card">
            <h2 className="card-title">MY <span>REVIEWS</span></h2>
            {loading && <p className="text-muted">Loading...</p>}
            {!loading && reviews.length === 0 && (
              <p className="text-muted">No reviews yet — complete jobs to receive ratings.</p>
            )}
            {reviews.map(r => (
              <div key={r.id} className="review-item">
                <div className="review-header">
                  <div className="review-stars">
                    {[1,2,3,4,5].map(n => (
                      <span key={n} style={{color: n <= r.rating ? 'var(--amber)' : 'rgba(244,196,48,0.2)'}}>★</span>
                    ))}
                    <span className="review-score">{r.rating}/5</span>
                  </div>
                  <span className="review-date">
                    {r.createdAt?.toDate ? r.createdAt.toDate().toLocaleDateString('en-GB', {day:'numeric',month:'short',year:'numeric'}) : ''}
                  </span>
                </div>
                {r.review && <p className="review-text">"{r.review}"</p>}
              </div>
            ))}
          </div>
        )}

      </div>

      {/* REFERRAL MODAL */}
      {referralModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setReferralModal(null)}>
          <div className="modal-box">
            <h2 className="modal-title">RECOMMEND A <span>CARRIER</span></h2>
            <p className="refer-modal-intro">
              Send another approved carrier a heads-up about this job.
              If they bid and win, you earn <strong>+15 Eco Credits</strong>!
            </p>
            <div className="modal-job-info">
              <span>{referralModal.serviceType}</span>
              <span>📍 {referralModal.pickup}</span>
            </div>
            <div className="form-group" style={{marginTop:'12px'}}>
              <input
                className="form-input"
                placeholder="🔍 Search carriers by name..."
                value={referralSearch}
                onChange={e => setReferralSearch(e.target.value)}
              />
            </div>
            <div className="refer-carrier-list">
              {referralCarriers.length === 0 && (
                <p className="text-muted" style={{padding:'12px 0',fontSize:'0.85rem',textAlign:'center'}}>Loading carriers...</p>
              )}
              {referralCarriers.length > 0 && (() => {
                const filtered = referralCarriers.filter(c =>
                  !referralSearch || (c.name || '').toLowerCase().includes(referralSearch.toLowerCase())
                );
                if (filtered.length === 0) return (
                  <p className="text-muted" style={{padding:'12px 0',fontSize:'0.85rem',textAlign:'center'}}>No carriers found.</p>
                );
                return filtered.map(c => (
                  <div
                    key={c.id}
                    className={`refer-carrier-row ${referralSelected?.id === c.id ? 'refer-selected' : ''}`}
                    onClick={() => setReferralSelected(c)}
                  >
                    <div className="refer-carrier-avatar">
                      {c.profilePhoto
                        ? <img src={c.profilePhoto} alt={c.name} className="refer-carrier-photo" />
                        : (c.name?.charAt(0) || '?')
                      }
                    </div>
                    <div className="refer-carrier-info">
                      <div className="refer-carrier-name">{c.name}</div>
                      <div className="refer-carrier-sub">
                        {c.location ? `📍 ${c.location}` : ''}
                        {c.rating ? ` · ★ ${c.rating}` : ''}
                        {c.jobs ? ` · ${c.jobs} jobs` : ''}
                      </div>
                    </div>
                    {referralSelected?.id === c.id && <span className="refer-check">✓</span>}
                  </div>
                ));
              })()}
            </div>
            <div className="modal-actions">
              <button
                className="btn btn-primary"
                style={{flex:1}}
                onClick={handleSubmitReferral}
                disabled={!referralSelected || referralSubmitting}
              >
                {referralSubmitting ? 'SENDING...' : 'SEND RECOMMENDATION'}
              </button>
              <button className="btn btn-secondary" onClick={() => setReferralModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* BID MODAL */}
      {bidModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setBidModal(null)}>
          <div className="modal-box">
            <h2 className="modal-title">PLACE YOUR <span>BID</span></h2>
            <div className="modal-job-info">
              <span>{bidModal.serviceType}</span>
              <span>{jobAreaLabel(bidModal) || 'Area unavailable'}</span>
              <span>{bidModal.serviceType === 'Scrap Collection' ? 'FREE — Scrap Collection' : (bidModal.budget || 'Open budget')}</span>
            </div>
            {stats.licenceNo
              ? <VerifiedBadge licenceNo={stats.licenceNo} size="md" />
              : <p style={{fontSize:'0.82rem', color:'var(--amber)', margin:'8px 0 0'}}>No waste carrier licence number on your profile. Email info@thewastegame.co.uk to add it before this bid is shown as licensed.</p>
            }
            <p style={{fontSize:'0.78rem',color:'var(--muted)',margin:'8px 0 16px'}}>
              {stats.licenceNo ? 'Your waste carrier licence number is shown with your bid.' : 'Bids store your real licence number only — never a placeholder.'}{' '}
              <RegisterCheck />
            </p>
            {bidModal.serviceType === 'Scrap Collection' ? (
              <p style={{fontSize:'0.85rem',color:'var(--teal)',background:'var(--teal-dim)',padding:'10px 14px',borderRadius:'var(--radius-sm)',border:'1px solid rgba(62,207,160,0.25)',marginBottom:'16px'}}>
                ℹ️ Scrap collection is free — you keep the scrap as your payment. Your bid price is £0.
              </p>
            ) : (
              <div className="form-group">
                <label className="form-label">Your Price *</label>
                <div className="input-prefix-wrap">
                  <span className="input-prefix">£</span>
                  <input className="form-input" type="number" min={MIN_BID_GBP} step="1" placeholder="e.g. 45"
                    value={bidForm.price} onChange={e => { setBidForm({...bidForm, price: e.target.value}); setBidError(''); }} />
                </div>
                <p style={{fontSize:'0.78rem',color: bidError ? '#e05c30' : 'var(--muted)',margin:'6px 0 0'}}>
                  {bidError || `Minimum bid is £${MIN_BID_GBP}`}
                </p>
              </div>
            )}
            <div className="form-group">
              <label className="form-label">ETA</label>
              <input className="form-input" placeholder="e.g. 45 mins"
                value={bidForm.eta} onChange={e => setBidForm({...bidForm, eta: e.target.value})} />
            </div>
            <div className="form-group">
              <label className="form-label">Note to Customer</label>
              <input className="form-input" placeholder="e.g. Will recycle everything possible"
                value={bidForm.note} onChange={e => setBidForm({...bidForm, note: e.target.value})} />
            </div>
            {bidModal.credits && (
              <div className="eco-credits-note">
                ♻️ This job earns <strong>+{bidModal.credits} Eco Credits</strong> on completion.
              </div>
            )}
            <div className="modal-actions">
              <button className="btn btn-primary" style={{flex:1}} onClick={handleBidSubmit} disabled={submitting}>
                {submitting ? 'SUBMITTING...' : (bidModal?.serviceType === 'Scrap Collection' ? 'ACCEPT SCRAP JOB' : 'SUBMIT BID')}
              </button>
              <button className="btn btn-secondary" onClick={() => setBidModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProviderDashboard;
